import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { accountQuery, useAuthSession } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import {
  AccountNav,
  Icon,
  profileMenuMoreItems,
  profileMenuPrimaryItems,
} from '@/ui';

export interface MenuProfileNavProps {
  className?: string;
  onClose?: () => void;
}

export function MenuProfileNav({ className, onClose }: MenuProfileNavProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const [expanded, setExpanded] = useState(false);

  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const primaryItems = profileMenuPrimaryItems(account, user);
  const moreItems = profileMenuMoreItems(account, user);

  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      <AccountNav items={primaryItems} onNavigate={onClose} />

      {expanded && moreItems.length > 0 && (
        <AccountNav items={moreItems} onNavigate={onClose} />
      )}

      {moreItems.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-center gap-1 rounded-md p-2 text-[13px] leading-normal font-normal text-text-secondary"
        >
          <span>{expanded ? label('Less', 'Less') : label('More', 'More')}</span>
          <Icon
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={16}
            className="text-icon-default"
          />
        </button>
      )}
    </div>
  );
}
